import React, { useContext } from 'react';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { BudgetBys } from 'business/constants';
import { useTranslation } from 'react-i18next';
import { usePrimaryColor } from 'styles/theme';
import Loading from 'components/Loading';
import Typography from '@mui/material/Typography';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { getAmountsData } from './dataHelpers';
import { getColumns } from './gridHelpers';
import { BORDER_LEFT, PADDING_TITLE } from '../helpers/constants';
import { useGetOperationInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import NoDataState from 'ui/NoDataState/NoDataState';
import { useGetOperationBalance } from '../helpers/hooks';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { StatusSettingsContext } from '../../BudgetWithProvider/StatusSettingsContext';

type OperationBalancesProps = {
    operationSoftwareId: string;
    exercice: number;
    budgetBy: BudgetBys;
    budgetProposals: BudgetProposalT[];
};

const StyGridContainer = styled(Box, {
    name: 'StyGridContainer',
})({
    display: 'flex',
    flexDirection: 'column',
    height: '20rem',
    flexGrow: 1,
    '.ag-root-wrapper': {
        height: '100%',
    },
    '.ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    '.ag-header-group-cell > .ag-header-cell-comp-wrapper': {
        justifyContent: 'center',
    },
    '&.ag-theme-material .ag-header .ag-header-cell-text': {
        fontSize: '11.5px',
        fontWeight: '510',
        textTransform: 'capitalize',
    },
    ' .ag-header-group-cell-with-group, .ag-header-cell': {
        ...BORDER_LEFT,
        justifyContent: 'center',
    },
    '.ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '9px',
        fontSize: '0.85em',
        justifyContent: 'center',
    },
});

const Layout = ({ children }: { children: React.ReactNode }) => {
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    return (
        <StyGridContainer className="ag-theme-material">
            <Typography sx={{ ...PADDING_TITLE }} variant="h5">
                {t('secondaryBudgetAxisElement360.balances', {
                    context: sourceTable,
                })}
            </Typography>
            {children}
        </StyGridContainer>
    );
};

export default function OperationBalances({
    operationSoftwareId,
    exercice,
    budgetBy,
    budgetProposals,
}: OperationBalancesProps): React.ReactElement {
    const { t } = useTranslation();
    const statusesSettings = useContext(StatusSettingsContext);

    const primaryColor = usePrimaryColor();
    const { data: budgetLinesSumsData, isLoading: isBudgetLinesLoading } =
        useGetOperationBalance(operationSoftwareId, budgetBy, exercice);

    const { data: inscriptionsData, isLoading: isInscriptionsLoading } =
        useGetOperationInscriptions({
            budgetBy,
            operationSoftwareId,
            exercice,
        });
    if (isBudgetLinesLoading || isInscriptionsLoading) {
        return (
            <Layout>
                <Loading />
            </Layout>
        );
    }
    if (!inscriptionsData) {
        return (
            <Layout>
                <NoDataState sx={{ height: '12rem' }} />
            </Layout>
        );
    }

    const displayData = getAmountsData(
        inscriptionsData.data ?? [],
        budgetLinesSumsData ?? [],
        budgetProposals,
        exercice,
        statusesSettings,
    );

    const exerciceRange = 2;
    const columns = getColumns(exercice, exerciceRange, primaryColor, t);
    return (
        <Layout>
            <AgGridReact
                rowData={displayData}
                rowHeight={24}
                headerHeight={42}
                columnDefs={columns}
            />
        </Layout>
    );
}
